var _jsxFileName = "/media/data/Sandbox/soapbox-defake/app/soapbox/components/error-boundary.tsx";
import "core-js/modules/web.dom-collections.iterator.js";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { getSoapboxConfig } from 'soapbox/actions/soapbox';
import * as BuildConfig from 'soapbox/build-config';
import { Text, Stack } from 'soapbox/components/ui';
import { captureException } from 'soapbox/monitoring';
import KVStore from 'soapbox/storage/kv-store';
import sourceCode from 'soapbox/utils/code';
import { unregisterSw } from 'soapbox/utils/sw';
import SiteLogo from './site-logo';

const goHome = () => location.href = '/';

const mapStateToProps = state => {
  const {
    links,
    logo
  } = getSoapboxConfig(state);
  return {
    siteTitle: state.instance.title,
    logo,
    links
  };
};

class ErrorBoundary extends React.PureComponent {
  constructor() {
    super(...arguments);

    _defineProperty(this, "state", {
      hasError: false,
      error: undefined,
      componentStack: undefined,
      browser: undefined
    });

    _defineProperty(this, "textarea", null);

    _defineProperty(this, "setTextareaRef", c => {
      this.textarea = c;
    });

    _defineProperty(this, "handleCopy", () => {
      if (!this.textarea) return;
      this.textarea.select();
      this.textarea.setSelectionRange(0, 99999);
      document.execCommand('copy');
    });

    _defineProperty(this, "getErrorText", () => {
      const {
        error,
        componentStack
      } = this.state;
      return error + componentStack;
    });

    _defineProperty(this, "clearCookies", e => {
      localStorage.clear();
      sessionStorage.clear();
      KVStore.clear();

      if ('serviceWorker' in navigator) {
        e.preventDefault();
        unregisterSw().then(goHome).catch(goHome);
      }
    });
  }

  componentDidCatch(error, info) {
    captureException(error, {
      tags: {
        // Allow page crashes to be easily searched in Sentry.
        ErrorBoundary: 'yes'
      }
    });
    this.setState({
      hasError: true,
      error,
      componentStack: info && info.componentStack
    });
    import(
    /* webpackChunkName: "error" */
    'bowser').then(_ref => {
      let {
        default: Bowser
      } = _ref;
      this.setState({
        browser: Bowser.getParser(window.navigator.userAgent)
      });
    }).catch(() => {});
  }

  render() {
    const {
      browser,
      hasError
    } = this.state;
    const {
      children,
      links
    } = this.props;

    if (!hasError) {
      return children;
    }

    const isProduction = BuildConfig.NODE_ENV === 'production';
    const errorText = this.getErrorText();
    return /*#__PURE__*/React.createElement("div", {
      className: "h-screen pt-16 pb-12 flex flex-col bg-white dark:bg-primary-900",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 114,
        columnNumber: 7
      }
    }, /*#__PURE__*/React.createElement("main", {
      className: "flex-grow flex flex-col justify-center max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 115,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex-shrink-0 flex justify-center",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 116,
        columnNumber: 11
      }
    }, /*#__PURE__*/React.createElement("a", {
      href: "/",
      className: "inline-flex",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 117,
        columnNumber: 13
      }
    }, /*#__PURE__*/React.createElement(SiteLogo, {
      alt: "Logo",
      className: "h-12 w-auto cursor-pointer",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 118,
        columnNumber: 15
      }
    }))), /*#__PURE__*/React.createElement("div", {
      className: "py-8",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 122,
        columnNumber: 11
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "text-center max-w-xl mx-auto space-y-2",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 123,
        columnNumber: 13
      }
    }, /*#__PURE__*/React.createElement("h1", {
      className: "text-3xl font-extrabold text-gray-900 dark:text-gray-500 tracking-tight sm:text-4xl",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 124,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "alert.unexpected.message",
      defaultMessage: "Something went wrong.",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 125,
        columnNumber: 17
      }
    })), /*#__PURE__*/React.createElement("p", {
      className: "text-lg text-gray-700 dark:text-gray-600",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 127,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "alert.unexpected.body",
      defaultMessage: "We're sorry for the interruption. If the problem persists, please reach out to our support team. You may also try to {clearCookies} (this will log you out).",
      values: {
        clearCookies: /*#__PURE__*/React.createElement("a", {
          href: "/",
          onClick: this.clearCookies,
          className: "text-primary-600 dark:text-accent-blue hover:underline",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 133,
            columnNumber: 23
          }
        }, /*#__PURE__*/React.createElement(FormattedMessage, {
          id: "alert.unexpected.clear_cookies",
          defaultMessage: "clear cookies and browser data",
          __self: this,
          __source: {
            fileName: _jsxFileName,
            lineNumber: 134,
            columnNumber: 25
          }
        }))
      },
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 128,
        columnNumber: 17
      }
    })), /*#__PURE__*/React.createElement(Text, {
      theme: "muted",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 144,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "medium",
      tag: "span",
      theme: "muted",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 145,
        columnNumber: 17
      }
    }, sourceCode.displayName, ":"), ' ', sourceCode.version), /*#__PURE__*/React.createElement("div", {
      className: "mt-10",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 150,
        columnNumber: 15
      }
    }, /*#__PURE__*/React.createElement("a", {
      href: "/",
      className: "text-base font-medium text-primary-600 dark:text-accent-blue hover:underline",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 151,
        columnNumber: 17
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "alert.unexpected.return_home",
      defaultMessage: "Return Home",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 152,
        columnNumber: 19
      }
    }), /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 153,
        columnNumber: 19
      }
    }, " \u2192")))), !isProduction && /*#__PURE__*/React.createElement("div", {
      className: "py-16 max-w-lg mx-auto space-y-4",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 159,
        columnNumber: 15
      }
    }, errorText && /*#__PURE__*/React.createElement("textarea", {
      ref: this.setTextareaRef,
      className: "h-48 p-4 shadow-sm bg-gray-100 text-gray-900 dark:text-gray-100 dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-700 rounded-md font-mono",
      value: errorText,
      onClick: this.handleCopy,
      readOnly: true,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 161,
        columnNumber: 19
      }
    }), browser && /*#__PURE__*/React.createElement(Stack, {
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 171,
        columnNumber: 19
      }
    }, /*#__PURE__*/React.createElement(Text, {
      weight: "semibold",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 172,
        columnNumber: 21
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "alert.unexpected.browser",
      defaultMessage: "Browser",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 172,
        columnNumber: 45
      }
    })), /*#__PURE__*/React.createElement(Text, {
      theme: "muted",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 173,
        columnNumber: 21
      }
    }, browser.getBrowserName(), " ", browser.getBrowserVersion()))))), /*#__PURE__*/React.createElement("footer", {
      className: "flex-shrink-0 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 181,
        columnNumber: 9
      }
    }, /*#__PURE__*/React.createElement("nav", {
      className: "flex justify-center space-x-4",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 182,
        columnNumber: 11
      }
    }, links.get('status') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("a", {
      href: links.get('status'),
      className: "text-sm font-medium text-gray-700 dark:text-gray-600 hover:underline",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 185,
        columnNumber: 17
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "alert.unexpected.links.status",
      defaultMessage: "Status",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 186,
        columnNumber: 19
      }
    }))), links.get('help') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "inline-block border-l border-gray-300",
      "aria-hidden": "true",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 193,
        columnNumber: 17
      }
    }), /*#__PURE__*/React.createElement("a", {
      href: links.get('help'),
      className: "text-sm font-medium text-gray-700 dark:text-gray-600 hover:underline",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 194,
        columnNumber: 17
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "alert.unexpected.links.help",
      defaultMessage: "Help Center",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 195,
        columnNumber: 19
      }
    }))), links.get('support') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "inline-block border-l border-gray-300",
      "aria-hidden": "true",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 202,
        columnNumber: 17
      }
    }), /*#__PURE__*/React.createElement("a", {
      href: links.get('support'),
      className: "text-sm font-medium text-gray-700 dark:text-gray-600 hover:underline",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 203,
        columnNumber: 17
      }
    }, /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "alert.unexpected.links.support",
      defaultMessage: "Support",
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 204,
        columnNumber: 19
      }
    }))))));
  }

}

export default connect(mapStateToProps)(ErrorBoundary);